import React from 'react'
import styled, { css, StyledProps } from 'styled-components'
import { IconCheck } from '../../iconography/Icons/Icons'
import { warn } from '../../../debug/warn'
import { SectionBody, SectionContainer } from '../../misc/Section/Section'

type HeaderSize = 'small' | 'medium' | 'big'

const bottomBorderHeight = (props: { size: HeaderSize }) => {
  let height
  switch (props.size) {
    case 'small':
      height = '2px'
      break
    case 'big':
      height = '4px'
      break
    case 'medium':
    default:
      height = '3px'
  }
  return {
    height,
  }
}

const headerSizeCss = (props: { size: HeaderSize }) => {
  switch (props.size) {
    case 'small':
      return css`
        height: 48px;
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
      `
    case 'big':
      return css`
        height: 96px;
        font-weight: 600;
        font-size: 24px;
        line-height: 36px;
      `
    case 'medium':
    default:
      return css`
        height: 72px;
        font-weight: 600;
        font-size: 20px;
        line-height: 28px;
      `
  }
}

const StepsHeaderContainer = styled.div<StyledProps<{ completed: boolean; size: HeaderSize }>>`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  color: ${props => props.theme.color.secondary200};
  border-bottom: 1px solid ${props => props.theme.color.secondary50};
  &::after {
    position: absolute;
    background-color: ${props => props.theme.color.primary600};
    content: '';
    width: 100%;
    display: ${props => (props.completed ? 'block' : 'none')};
    bottom: -1px;
    left: 0;
    ${bottomBorderHeight};
  }
  ${headerSizeCss}
`

type StepHeaderItemProps = StyledProps<{
  current?: boolean
  completed?: boolean
  disabled?: boolean
  size: HeaderSize
}>
const stepHeaderCss = (props: StepHeaderItemProps) => {
  let color = props.theme.color.textSecondary
  let fontWeight = 500
  if (props.completed) {
    color = props.theme.color.primary600
  } else if (props.current) {
    color = props.theme.color.onPrimaryDark
    fontWeight = 600
  }
  return css`
    color: ${color};
    pointer-events: ${props.disabled ? 'none' : 'default'};
    cursor: ${props.disabled ? 'not-allowed' : 'pointer'};
    font-weight: ${fontWeight};
  `
}

const CheckIconWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2em;
  padding: 0 4px;
  right: 0;
  top: 0;
  bottom: 0;
  ${props => props.theme.media.mobile`
    display: none;
  `}
  > * {
    width: 1em;
    height: auto;
  }
`

const StepHeaderItemContainer = styled.div<StepHeaderItemProps>`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: 2em;
  padding-right: 2em;
  white-space: nowrap;
  justify-content: center;
  width: 100%;
  &::after {
    position: absolute;
    background-color: ${props => props.theme.color.primary600};
    content: '';
    width: 100%;
    display: ${props => (props.completed || props.current ? 'block' : 'none')};
    bottom: -1px;
    left: 0;
    ${bottomBorderHeight};
  }
  ${stepHeaderCss}

  ${props => props.theme.media.mobile`
    padding-left: 1em;
    padding-right: 1em;
  `}
`

type StepItemProps = {
  id: string
  className?: string
  current?: boolean
  completed?: boolean
  title: string
  disabled?: boolean
  checkIcon?: boolean | React.ReactNode
  name?: number | string
  onClick(): void
  children?: React.ReactNode
  headerSize: HeaderSize
}
const StepHeaderItem: React.FC<StepItemProps> = ({
  className,
  current,
  completed,
  title,
  disabled = false,
  checkIcon = true,
  headerSize,
  onClick,
}) => {
  const checkIconComponents = typeof checkIcon === 'boolean' ? <IconCheck /> : checkIcon

  return (
    <StepHeaderItemContainer
      className={className}
      current={current}
      completed={completed}
      onClick={onClick}
      disabled={disabled}
      size={headerSize}
    >
      {title}
      {checkIcon ? <CheckIconWrapper>{completed && checkIconComponents}</CheckIconWrapper> : null}
    </StepHeaderItemContainer>
  )
}

type StepProps = Omit<StepItemProps, 'onClick' | 'headerSize'>
export const StepFormStep: React.FC<StepProps> = _props => {
  const props = _props as StepItemProps
  return (
    <StepHeaderItem
      current={props.current}
      completed={props.completed}
      title={props.title}
      onClick={props.onClick}
      disabled={props.disabled}
      className={props.className}
      checkIcon={props.checkIcon}
      id={props.id}
      headerSize={props.headerSize}
    />
  )
}

export const StepForm: React.FC<{
  step: string
  completed: boolean
  completeContent?: React.ReactElement
  lockOnLastStep?: boolean
  onTabClick(step: string): void
  headerSize?: HeaderSize
}> = ({ step, completed, children, completeContent, onTabClick, lockOnLastStep = true, headerSize = 'medium' }) => {
  const nodes = React.Children.toArray(children) as React.ReactElement<StepItemProps>[]
  const current = nodes.find(n => n.props.id === step)

  if (typeof children === 'string') {
    warn(`StepSection: invalid children`)
    return null
  }

  if (!current) {
    warn(
      `StepSection: couldn't find step container with passed step id. Steps total "${nodes
        .map(n => n.props.id)
        .join(', ')}", step id passed "${step}"`,
    )
  }

  return (
    <SectionContainer noDivider>
      <StepsHeaderContainer completed={completed} size={headerSize}>
        {nodes.map((node, idx) => {
          return React.cloneElement(node, {
            onClick: () => !(lockOnLastStep && completed) && onTabClick(node.props.id),
            current: step === node.props.id,
            completed: nodes.findIndex(n => n.props.id === step) > idx || completed,
            key: `step-${node.props.id}`,
            headerSize: headerSize,
          })
        })}
      </StepsHeaderContainer>
      <SectionBody paddedOnMobile id={current?.props?.id} noGrid>
        {completed && completeContent ? completeContent : current?.props?.children || null}
      </SectionBody>
    </SectionContainer>
  )
}
